import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { Inbox, Loader2, Check, X, Building2, Mail } from "lucide-react";

/**
 * Access-request approval queue (iter110).
 *
 * Lists pending submissions from the public /request-access form and
 * lets the Owner / org_admin approve (provisions a viewer-level user
 * with email invite) or reject (logs decision with optional notes).
 */
export default function AccessRequestsQueue() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/access-requests?status=${tab}`);
      setRows(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tab]);

  const act = async (id, action) => {
    let notes = "";
    if (action === "reject") {
      notes = window.prompt("Optional rejection notes (visible only to operators):") || "";
    }
    setBusy((b) => ({ ...b, [id]: action }));
    try {
      const { data } = await api.post(
        `/admin/access-requests/${id}/${action}`,
        action === "reject" ? { notes } : {},
      );
      toast.success(
        action === "approve"
          ? `Approved — temporary password emailed to ${data?.user?.email || "user"}.`
          : "Request rejected.",
      );
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || `${action} failed`);
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Access requests" description="Approve incoming access requests." path="/admin/access-requests" />
      <Navbar />

      <main className="mx-auto max-w-6xl px-6 py-10">
        <header className="mb-8">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-ek-ink/60">Admin · governance</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-ek-ink">Access requests</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ek-ink/70">
            Submissions from the public <code className="rounded bg-white px-1.5 py-0.5 text-xs">/request-access</code> form. Approving provisions a viewer-level user and emails a temporary password; rejecting closes the row with optional notes.
          </p>
        </header>

        <div className="mb-6 flex gap-2 border-b border-ek-ink/10">
          {["pending", "approved", "rejected"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`access-queue-tab-${t}`}
              className={`-mb-px px-4 py-2 text-sm font-medium capitalize transition-colors ${tab === t ? "border-b-2 border-ek-purple text-ek-purple" : "text-ek-ink/55 hover:text-ek-ink"}`}
            >
              {t}
            </button>
          ))}
        </div>

        <section className="rounded-md border border-ek-ink/10 bg-white">
          {loading ? (
            <div className="flex items-center justify-center p-10 text-ek-ink/50" data-testid="access-queue-loading">
              <Loader2 className="animate-spin" size={20} />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center" data-testid="access-queue-empty">
              <Inbox className="mx-auto mb-3 text-ek-ink/30" size={36} />
              <p className="text-sm font-medium text-ek-ink/70">No {tab} requests</p>
            </div>
          ) : (
            <ul className="divide-y divide-ek-ink/5" data-testid="access-queue-list">
              {rows.map((r) => (
                <li key={r.request_id} className="p-5" data-testid={`access-row-${r.request_id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-ek-ink">{r.name}</p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ek-ink/55">
                        <span className="inline-flex items-center gap-1"><Mail size={11} />{r.email}</span>
                        {r.company && <span className="inline-flex items-center gap-1"><Building2 size={11} />{r.company}</span>}
                        {r.role_interest && <span>· {r.role_interest}</span>}
                        <span>· submitted {(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                      </p>
                      {r.message && (
                        <p className="mt-2 max-w-3xl rounded bg-ek-canvas px-3 py-2 text-sm text-ek-ink/75">
                          {r.message}
                        </p>
                      )}
                      {r.decision_notes && (
                        <p className="mt-2 text-xs text-ek-ink/55">
                          <b>Operator notes:</b> {r.decision_notes}
                          {r.decided_by_email && <> · by {r.decided_by_email}</>}
                        </p>
                      )}
                    </div>
                    {tab === "pending" && (
                      <div className="flex shrink-0 gap-2">
                        <button
                          onClick={() => act(r.request_id, "approve")}
                          disabled={!!busy[r.request_id]}
                          data-testid={`access-approve-${r.request_id}`}
                          className="ek-btn flex items-center gap-1.5 disabled:opacity-50"
                        >
                          {busy[r.request_id] === "approve" ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Approve
                        </button>
                        <button
                          onClick={() => act(r.request_id, "reject")}
                          disabled={!!busy[r.request_id]}
                          data-testid={`access-reject-${r.request_id}`}
                          className="inline-flex items-center gap-1.5 rounded border border-ek-ink/15 px-3 py-2 text-sm font-medium text-ek-ink/70 hover:border-red-400 hover:text-red-600"
                        >
                          <X size={14} /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
